import { ChangeEvent, useState } from 'react';

import { useDebouncedCallback } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { AddNoCircle } from '@mint-lib/icons';
import { Button, Card } from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { WhitelistedEmails as WhitelistedEmailType } from '@mint-sdk/api-data-ingestion';

import { EmptyState } from '../components/atoms/EmptyState.jsx';
import { Tooltip } from '../components/atoms/Tooltip.jsx';
import { PageHeader } from '../components/molecules/PageHeader.jsx';
import { WhitelistedEmailDeleteDialog } from '../components/molecules/WhitelistedEmailDeleteDialog.jsx';
import { WhitelistedEmailDialog } from '../components/molecules/WhitelistedEmailDialog.jsx';
import { WhitelistedEmailsTable } from '../components/molecules/WhitelistedEmailsTable.jsx';
import { useWhitelistedEmailsRouteApi } from '../hooks/useImportHubRouteApi.js';
import { useIsReadOnlyUser } from '../hooks/useIsReadOnlyUser.js';
import {
  useWhitelistedEmails,
  useWhitelistedEmailsMeta,
} from '../queries/index.js';

type DialogState =
  | { type: 'closed' }
  | { type: 'add' }
  | { type: 'edit'; email: WhitelistedEmailType.WhitelistedEmail }
  | { type: 'delete'; email: WhitelistedEmailType.WhitelistedEmail };

export function WhitelistedEmails() {
  const { t } = useTranslation('@myn/import-hub');
  const whitelistedEmailsRoute = useWhitelistedEmailsRouteApi();
  const searchParams = whitelistedEmailsRoute.useSearch();
  const navigate = whitelistedEmailsRoute.useNavigate();

  const { data, isLoading } = useWhitelistedEmails(searchParams.search);
  const { totalItems, canBeCreated } = useWhitelistedEmailsMeta();
  const isReadOnlyUser = useIsReadOnlyUser();

  const [dialog, setDialog] = useState<DialogState>({ type: 'closed' });
  const [searchValue, setSearchValue] = useState(searchParams.search ?? '');

  const debouncedNavigate = useDebouncedCallback((value: string) => {
    navigate({
      search: (prev) => ({ ...prev, search: value }),
      replace: true,
    });
  }, 300);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    debouncedNavigate(e.target.value);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    navigate({
      search: (prev) => ({ ...prev, search: '' }),
      replace: true,
    });
  };

  const isAddDisabled = !canBeCreated || isReadOnlyUser;
  const isEmpty = !isLoading && totalItems === 0;
  const isEmptySearch =
    !isLoading && (data?.length ?? 0) === 0 && !!searchParams.search;

  return (
    <>
      <PageHeader
        title={t('Trusted emails')}
        description={t(
          'Manage trusted email addresses for auto-import in this workspace. Use this list for external or system emails that are not added as workspace users but should still be considered safe.',
        )}
        backLink={true}
      />
      {isEmpty && !searchParams.search ? (
        <EmptyState
          icon="IllustrationEmptyState"
          text={t('No trusted emails added yet')}
          description={t(
            'Add an email address to allow auto-imports from external or system senders.',
          )}
          className="border-none bg-transparent shadow-none"
        >
          {!isReadOnlyUser && (
            <Button
              onClick={() => setDialog({ type: 'add' })}
              className="mt-4"
              data-dd-action-name="Add email"
            >
              <AddNoCircle className="size-4" />
              {t('Add email')}
            </Button>
          )}
        </EmptyState>
      ) : (
        <>
          <Card
            className="mt-4 w-full"
            contentClassName="flex items-center gap-4"
            hover="noHover"
          >
            <Tooltip
              description={t(
                'You have reached the maximum number of trusted emails',
              )}
              disabled={canBeCreated || isReadOnlyUser}
              asChild
            >
              <Button
                onClick={() => setDialog({ type: 'add' })}
                disabled={isAddDisabled}
                data-dd-action-name="Add email"
              >
                <AddNoCircle className="size-4" />
                {t('Add email')}
              </Button>
            </Tooltip>
            <SearchBar
              value={searchValue}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
              placeholder={t('Search')}
              size="medium"
              className="h-8 flex-1 shrink-0"
            />
          </Card>
          {isEmptySearch ? (
            <EmptyState
              className="mt-4"
              icon="IllustrationNoResults"
              text={t('No results')}
              description={t('Adjust your search to find results.')}
            />
          ) : (
            <div className="mt-4">
              <WhitelistedEmailsTable
                data={data}
                isLoading={isLoading}
                onEdit={(email) => setDialog({ type: 'edit', email })}
                onDelete={(email) => setDialog({ type: 'delete', email })}
              />
            </div>
          )}
        </>
      )}

      {(dialog.type === 'add' || dialog.type === 'edit') && (
        <WhitelistedEmailDialog
          email={dialog.type === 'edit' ? dialog.email : undefined}
          existingEmails={data}
          onClose={() => setDialog({ type: 'closed' })}
        />
      )}

      {dialog.type === 'delete' && (
        <WhitelistedEmailDeleteDialog
          email={dialog.email}
          onClose={() => setDialog({ type: 'closed' })}
        />
      )}
    </>
  );
}
